import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { k: "kpi", label: "KPI card" },
  { k: "chart-bar", label: "Bar chart", ct: "bar" },
  { k: "chart-line", label: "Line chart", ct: "line" },
  { k: "chart-area", label: "Area chart", ct: "area" },
  { k: "chart-pie", label: "Pie chart", ct: "pie" },
  { k: "table", label: "Table" },
];
const AGG = ["sum", "avg", "count", "min", "max"];
const OPS = [
  { k: "eq", label: "equals" },
  { k: "neq", label: "not equals" },
  { k: "gt", label: ">" },
  { k: "gte", label: ">=" },
  { k: "lt", label: "<" },
  { k: "lte", label: "<=" },
];

function pickInitialType(w) {
  if (!w) return "chart-bar";
  if (w.type === "kpi") return "kpi";
  if (w.type === "table") return "table";
  return "chart-" + (w.chart_type || "bar");
}

export default function WidgetEditor({ widget, datasets, defaultDatasetId, onClose, onSave, onDelete }) {
  const isNew = !widget;
  const [typeKey, setTypeKey] = useState(pickInitialType(widget));
  const [datasetId, setDatasetId] = useState(widget?.dataset_id || defaultDatasetId || (datasets[0] && datasets[0].id) || "");
  const [ds, setDs] = useState(null);
  const [title, setTitle] = useState(widget?.title || "New widget");
  const [x, setX] = useState(widget?.x || "");
  const [y, setY] = useState(widget?.y || "");
  const [agg, setAgg] = useState(widget?.agg || "sum");
  const [columns, setColumns] = useState(widget?.columns || []);
  const [limit, setLimit] = useState(widget?.limit || 25);
  const [f, setF] = useState(widget?.filter || { column: "", op: "eq", value: "" });
  const [useFilter, setUseFilter] = useState(!!widget?.filter);

  useEffect(() => {
    if (!datasetId) return;
    api.get(`/datasets/${datasetId}`).then(({ data }) => setDs(data)).catch(() => {});
  }, [datasetId]);

  // when switching dataset, reset x/y if no longer valid
  useEffect(() => {
    if (!ds) return;
    if (x && !ds.columns.includes(x)) setX("");
    if (y && !ds.columns.includes(y)) setY("");
    if (!x && ds.columns.length) {
      const firstCat = ds.columns.find(c => ds.column_types[c] === "string") || ds.columns[0];
      setX(firstCat);
    }
    if (!y) {
      const firstNum = ds.columns.find(c => ds.column_types[c] === "number");
      if (firstNum) setY(firstNum);
    }
  }, [ds]);

  const numCols = ds ? ds.columns.filter(c => ds.column_types[c] === "number") : [];
  const allCols = ds ? ds.columns : [];

  const save = () => {
    const cfg = { id: widget?.id, title: title || "Untitled", dataset_id: datasetId };
    if (typeKey === "kpi") {
      cfg.type = "kpi"; cfg.y = y || null; cfg.agg = agg;
    } else if (typeKey === "table") {
      cfg.type = "table"; cfg.columns = columns.length ? columns : allCols.slice(0, 6); cfg.limit = Number(limit) || 25;
    } else {
      cfg.type = "chart";
      cfg.chart_type = typeKey.replace("chart-", "");
      cfg.x = x; cfg.y = y || null; cfg.agg = agg;
    }
    if (useFilter && f.column) {
      cfg.filter = { column: f.column, op: f.op || "eq", value: f.value };
    }
    onSave(cfg);
  };

  const toggleCol = (c) => {
    setColumns(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="widget-editor">
      <div className="bg-white border border-[var(--border)] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto scroll-thin shadow-xl">
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="label-caps mb-1">{isNew ? "Add widget" : "Edit widget"}</div>
            <h2 className="font-display text-xl font-medium">{title || "New widget"}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="label-caps block mb-2">Title</label>
            <input data-testid="w-title" value={title} onChange={(e) => setTitle(e.target.value)} className="input-base" />
          </div>

          <div>
            <label className="label-caps block mb-2">Data source</label>
            <select data-testid="w-dataset" value={datasetId} onChange={(e) => setDatasetId(e.target.value)} className="input-base">
              {datasets.map(d => <option key={d.id} value={d.id}>{d.name} ({d.row_count} rows)</option>)}
            </select>
          </div>

          <div>
            <label className="label-caps block mb-2">Visualization</label>
            <div className="grid grid-cols-3 gap-2">
              {TYPES.map(t => (
                <button key={t.k} type="button" onClick={() => setTypeKey(t.k)} data-testid={`w-type-${t.k}`}
                  className={`px-3 py-2 rounded-lg border text-sm transition-colors ${typeKey === t.k ? "border-[var(--primary)] bg-[var(--primary-soft)] text-[var(--primary)] font-medium" : "border-[var(--border)] hover:border-[var(--border-strong)] text-slate-700"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {typeKey !== "table" && typeKey !== "kpi" && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-caps block mb-2">X (dimension)</label>
                <select data-testid="w-x" value={x} onChange={(e) => setX(e.target.value)} className="input-base mono">
                  {allCols.map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="label-caps block mb-2">Y (measure)</label>
                <select data-testid="w-y" value={y} onChange={(e) => setY(e.target.value)} className="input-base mono">
                  <option value="">— count rows —</option>
                  {numCols.map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="label-caps block mb-2">Aggregation</label>
                <select data-testid="w-agg" value={agg} onChange={(e) => setAgg(e.target.value)} className="input-base">
                  {AGG.map(a => <option key={a} value={a}>{a}</option>)}
                </select></div>
            </div>
          )}
          {typeKey === "kpi" && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-caps block mb-2">Measure</label>
                <select data-testid="w-y" value={y} onChange={(e) => setY(e.target.value)} className="input-base mono">
                  <option value="">— count of rows —</option>
                  {numCols.map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="label-caps block mb-2">Aggregation</label>
                <select data-testid="w-agg" value={agg} onChange={(e) => setAgg(e.target.value)} className="input-base">
                  {AGG.map(a => <option key={a} value={a}>{a}</option>)}
                </select></div>
            </div>
          )}
          {typeKey === "table" && ds && (
            <div>
              <label className="label-caps block mb-2">Columns ({columns.length || allCols.slice(0, 6).length})</label>
              <div className="border border-[var(--border)] rounded-lg p-3 max-h-40 overflow-y-auto scroll-thin grid grid-cols-2 gap-1">
                {allCols.map(c => (
                  <label key={c} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={columns.includes(c) || (!columns.length && allCols.slice(0, 6).includes(c))} onChange={() => toggleCol(c)} />
                    <span className="mono truncate">{c}</span>
                  </label>
                ))}
              </div>
              <div className="mt-3">
                <label className="label-caps block mb-2">Row limit</label>
                <input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} className="input-base mono w-32" min={1} max={500} />
              </div>
            </div>
          )}

          <div>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input type="checkbox" checked={useFilter} onChange={(e) => setUseFilter(e.target.checked)} data-testid="w-filter-toggle" />
              <span className="text-sm font-medium text-slate-900">Filter data</span>
            </label>
            {useFilter && (
              <div className="grid grid-cols-3 gap-2">
                <select data-testid="w-filter-col" value={f.column} onChange={(e) => setF({...f, column: e.target.value})} className="input-base mono">
                  <option value="">— column —</option>
                  {allCols.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select data-testid="w-filter-op" value={f.op} onChange={(e) => setF({...f, op: e.target.value})} className="input-base">
                  {OPS.map(o => <option key={o.k} value={o.k}>{o.label}</option>)}
                </select>
                <input data-testid="w-filter-val" value={f.value} onChange={(e) => setF({...f, value: e.target.value})} className="input-base" placeholder="value" />
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-[var(--border)] flex justify-between items-center bg-slate-50/50">
          {!isNew && onDelete ? (
            <button onClick={() => { if (window.confirm("Delete this widget?")) onDelete(); }} data-testid="w-delete" className="text-sm text-slate-500 hover:text-[var(--danger)] inline-flex items-center gap-1">
              <Trash2 size={14} /> Delete
            </button>
          ) : <div />}
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-ghost">Cancel</button>
            <button onClick={save} data-testid="w-save" className="btn-primary"><Save size={14} /> Save widget</button>
          </div>
        </div>
      </div>
    </div>
  );
}
